/**
 * ReportTopBar
 *
 * Shared top bar for report sheet steppers.
 * Shows a back arrow on the left (when back is possible), a title in the center,
 * and a close button on the right.
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { ANIMATION_CONSTANTS } from '@/presentation/constants/animationConstants';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface ReportTopBarProps {
  title: string;
  showBack: boolean;
  onBack: () => void;
  onClose: () => void;
}

export const ReportTopBar: React.FC<ReportTopBarProps> = ({
  title,
  showBack,
  onBack,
  onClose,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  const buttonStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        paddingLeft: responsive.spacing.lg,
        paddingRight: responsive.spacing.lg,
        paddingTop: responsive.spacing.base,
        paddingBottom: responsive.spacing.base,
      }}
    >
      {/* Left: back button */}
      <div style={{
        // reserve space even when back button is hidden to prevent title from shifting
        width: responsive.spacing['3xl'],
        display: 'flex',
        alignItems: 'center'
      }}>
        {showBack && (
          <button {...qaAttr({ id: 'report-top-bar-back' })} onClick={onBack} style={buttonStyle}>
            <AssetIcon path="/assets/images/arrow_up.png" rotationAngle={ANIMATION_CONSTANTS.rotation.left} />
          </button>
        )}
      </div>

      {/* Center: title */}
      <span
        {...qaAttr({ id: 'report-top-bar-title', value: title })}
        style={{
          flex: 1,
          textAlign: 'center',
          color: theme.text.primary,
          fontSize: responsive.font.base,
          fontWeight: responsive.fontWeight.semibold,
        }}
      >
        {title}
      </span>

      {/* Right: close button */}
      <div style={{
        // keep right side width consistent with left side to prevent title shifting
        width: responsive.spacing['3xl'],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end'
      }}>
        <button {...qaAttr({ id: 'report-top-bar-close' })} onClick={onClose} style={buttonStyle}>
          <AssetIcon path="/assets/images/close.png" />
        </button>
      </div>
    </div>
  );
};
